import { useState } from "react";
import { Clock, CheckCircle2, XCircle, Hourglass, Users, Copy, Check } from "lucide-react";

const statusConfig = {
  PendingFirstEvent: { label: "Awaiting first event", icon: Hourglass, className: "text-secondary bg-secondary/10" },
  Qualified: { label: "Qualified", icon: CheckCircle2, className: "text-success bg-success/10" },
  Expired: { label: "Expired", icon: Clock, className: "text-text-muted bg-bg-elevated" },
  Voided: { label: "Voided", icon: XCircle, className: "text-danger bg-danger/10" },
};

const formatCurrency = (amount) =>
  amount == null ? "—" : `R${amount.toFixed(2)}`;

// `rows` — one entry per referral, merged with any matching commission
// from the earnings endpoint:
// { hubName, status, grossRevenue, yourShare }
const HubStatusTable = ({ rows, referralLink }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!referralLink) return;
    await navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (rows.length === 0) {
    return (
      <div className="bg-bg-surface border border-border rounded-2xl p-12 text-center">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
          <Users size={24} className="text-primary" />
        </div>
        <h3 className="text-text-primary font-semibold mb-2">
          No hubs referred yet
        </h3>
        <p className="text-text-muted text-sm mb-6 max-w-xs mx-auto">
          Share your referral link with an organizer to start earning once
          their first paid event goes live.
        </p>
        {referralLink && (
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-2 ingroove-gradient-bg text-white font-semibold text-sm px-5 py-2.5 rounded-xl hover:opacity-90 transition shadow-glow"
          >
            {copied ? <Check size={15} /> : <Copy size={15} />}
            {copied ? "Link copied" : "Copy your link"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-bg-surface border border-border rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-text-muted text-xs uppercase tracking-wider">
            <th className="text-left px-6 py-4 font-medium">Hub</th>
            <th className="text-left px-6 py-4 font-medium">Status</th>
            <th className="text-right px-6 py-4 font-medium">Event revenue</th>
            <th className="text-right px-6 py-4 font-medium">Your share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const status = statusConfig[row.status] ?? statusConfig.PendingFirstEvent;
            const Icon = status.icon;
            return (
              <tr key={i} className="border-b border-border last:border-0">
                <td className="px-6 py-4 text-text-primary font-medium">{row.hubName}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${status.className}`}>
                    <Icon size={13} />
                    {status.label}
                  </span>
                </td>
                <td className="px-6 py-4 text-right text-text-secondary font-mono">
                  {formatCurrency(row.grossRevenue)}
                </td>
                <td className="px-6 py-4 text-right text-primary font-mono font-semibold">
                  {formatCurrency(row.yourShare)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default HubStatusTable;
